import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { createTheme } from '@mui/material/styles';

const ThemeModeContext = createContext();

const STORAGE_KEY = 'app-theme-mode';

function buildTheme(mode) {
  const isDark = mode === 'dark';
  // Layered dark surfaces — depth comes from tone + shadow, not borders:
  //   Level 0 page bg #0d0f14 / Level 1 sidebar+header #14171d /
  //   Level 2 cards+panels #1a1e26 / Level 3 hover+active #20252e
  const surface1 = '#14171d';
  const surface2 = '#1a1e26';
  const surface3 = '#20252e';
  const panelShadow = '0 1px 3px rgba(0,0,0,.4)';
  return createTheme({
    palette: {
      mode,
      primary: {
        main: '#1976d2',
      },
      secondary: {
        main: '#dc004e',
      },
      background: {
        default: isDark ? '#0d0f14' : '#f5f5f5',
        paper: isDark ? surface2 : '#ffffff',
      },
      divider: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.12)',
      ...(isDark && {
        action: {
          hover: surface3,
          selected: '#262c37',
          focus: 'rgba(255,255,255,0.12)',
        },
      }),
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontSize: '2.2rem',
        fontWeight: 500,
      },
      h2: {
        fontSize: '1.8rem',
        fontWeight: 500,
      },
      h3: {
        fontSize: '1.5rem',
        fontWeight: 500,
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            fontWeight: 'bold',
          },
          contained: {
            color: '#ffffff',
            '&:hover': {
              color: '#ffffff',
            },
          },
          containedInherit: {
            color: isDark ? '#ffffff' : 'rgba(0, 0, 0, 0.87)',
            '&:hover': {
              color: isDark ? '#ffffff' : 'rgba(0, 0, 0, 0.87)',
            },
          },
        },
      },
      ...(isDark && {
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundImage: 'none',
            },
            outlined: {
              border: 'none',
              boxShadow: panelShadow,
            },
          },
        },
        MuiAppBar: {
          styleOverrides: {
            colorPrimary: {
              backgroundColor: surface1,
              backgroundImage: 'none',
              boxShadow: panelShadow,
            },
            colorDefault: {
              backgroundColor: surface1,
              backgroundImage: 'none',
              boxShadow: panelShadow,
            },
          },
        },
        MuiDrawer: {
          styleOverrides: {
            paper: {
              backgroundColor: surface1,
              backgroundImage: 'none',
              borderRight: 'none',
            },
          },
        },
        MuiTableRow: {
          styleOverrides: {
            root: {
              '&.MuiTableRow-hover:hover': {
                backgroundColor: surface3,
              },
            },
          },
        },
        MuiAccordion: {
          styleOverrides: {
            root: {
              backgroundImage: 'none',
              boxShadow: panelShadow,
              '&:before': {
                backgroundColor: 'transparent',
              },
            },
          },
        },
        MuiToggleButton: {
          styleOverrides: {
            root: {
              textTransform: 'none',
              border: 'none',
              color: 'rgba(255,255,255,0.65)',
              '&.Mui-selected': {
                backgroundColor: '#1976d2',
                color: '#ffffff',
                '&:hover': {
                  backgroundColor: '#1565c0',
                },
              },
            },
          },
        },
      }),
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            boxShadow: isDark
              ? panelShadow
              : '0 4px 6px rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },
  });
}

export function ThemeModeProvider({ children }) {
  const [mode, setMode] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'dark' || saved === 'light') return saved;
    return 'light';
  });

  const theme = buildTheme(mode);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, mode);
  }, [mode]);

  const toggleMode = useCallback(() => {
    setMode(prev => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  const setThemeMode = useCallback((newMode) => {
    setMode(newMode === 'dark' ? 'dark' : 'light');
  }, []);

  return (
    <ThemeModeContext.Provider value={{ mode, toggleMode, setThemeMode, theme }}>
      {children}
    </ThemeModeContext.Provider>
  );
}

export function useThemeMode() {
  const ctx = useContext(ThemeModeContext);
  if (!ctx) {
    return {
      mode: 'light',
      toggleMode: () => {},
      setThemeMode: () => {},
      theme: buildTheme('light'),
    };
  }
  return ctx;
}
